import { useEffect, useMemo, useState } from 'react';
import { useSession } from '@sdk/session/SessionContext';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { UserPickerOption } from 'sling-sdk/src/components/forms/personaPickers/PersonaPicker/personaPicker.types';

import { useNavigate } from '@web/Router/router.hooks';
import { useConversationCreateRestrictions } from '@web/conversations/ComposeConversation/composeConversation.hooks';
import { useConversationMembersField } from '@web/conversations/ComposeConversation/ConversationMembersField/conversationMembersField.hooks';
import { useCreateConversation } from '@web/conversations/hooks/useCreateConversation';

export const useCreatePrivateConversation = () => {
  useCreatePrivateConversationRestrictions();

  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useSession();
  const navigate = useNavigate();
  const { showErrorToast } = useToast();
  const { mutateAsync } = useCreateConversation();
  const { options } = useConversationMembersField();
  const { canOnlyStartPrivateConversation } = useConversationCreateRestrictions();

  const userOptions = useMemo(() => {
    const filteredOptions = options.filter(
      (option) =>
        option.id !== currentUser.id &&
        option.type === 'user' &&
        option.title.toLowerCase().includes(searchQuery.toLowerCase())
    ) as UserPickerOption[];

    return filteredOptions.slice().sort((a, b) => a.title.localeCompare(b.title));
  }, [currentUser.id, options, searchQuery]);

  const addPrivateConversationMutation = (userId: ID) =>
    mutateAsync(
      { members: [userId] },
      {
        onSuccess: (conversation) => {
          navigate.toConversationDetails({
            params: { id: conversation.id, slug: conversation.slug },
          });
        },
        onError: (error) => {
          const errorMsg = getErrorForToast(error);
          showErrorToast(errorMsg);
        },
      }
    );

  const submit = async (userId: ID) => {
    await addPrivateConversationMutation(userId);
  };

  const cancel = () => {
    if (canOnlyStartPrivateConversation) {
      navigate.toConversationRoot();
    } else {
      navigate.toConversationCompose();
    }
  };

  return { userOptions, searchQuery, setSearchQuery, submit, cancel };
};

export const useCreatePrivateConversationRestrictions = () => {
  const navigate = useNavigate();
  const { canStartPrivateConversation } = useConversationCreateRestrictions();

  useEffect(() => {
    if (!canStartPrivateConversation) {
      navigate.toConversationRoot();
    }
  }, [navigate, canStartPrivateConversation]);
};
